/**
 * The country page's views: Board (clean URL) · Day timeline · Live ·
 * Gateways · Errors (19/10/2026) · How it works (the tutorial, with a dot until the viewer opened it once).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Activity, CalendarRange, GraduationCap, Layers, RadioTower, ShieldAlert } from 'lucide-react';

import { useTutorialProgress } from '@/features/drop-generator/components/tutorial/use-tutorial-progress';
import { cn } from '@/lib/utils';

export type CountryTab = 'board' | 'timeline' | 'live' | 'gateways' | 'errors' | 'tutorial';

const TABS: Array<{ tab: CountryTab; label: string; Icon: typeof Layers }> = [
  { tab: 'board', label: 'Board', Icon: Layers },
  { tab: 'timeline', label: 'Day timeline', Icon: CalendarRange },
  { tab: 'live', label: 'Live', Icon: Activity },
  { tab: 'gateways', label: 'Gateways', Icon: RadioTower },
  { tab: 'errors', label: 'Errors', Icon: ShieldAlert },
  { tab: 'tutorial', label: 'How it works', Icon: GraduationCap },
];

export const CountryTabs: FC<{
  tab: CountryTab;
  onSelect: (tab: CountryTab) => void;
}> = ({ tab, onSelect }) => {
  const { hasOpened } = useTutorialProgress();
  return (
    <div role="tablist" aria-label="Views" className="flex items-center gap-1 self-start rounded-lg border border-border/60 bg-muted/30 p-1">
      {TABS.map(({ tab: item, label, Icon }) => (
        <button
          key={item}
          type="button"
          role="tab"
          aria-selected={tab === item}
          aria-label={label}
          title={label}
          onClick={() => onSelect(item)}
          className={cn(
            'relative inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors',
            tab === item ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          <span className={cn(item === tab ? 'inline' : 'hidden md:inline')}>{label}</span>
          {item === 'tutorial' && !hasOpened ? (
            <span aria-label="new" className="h-1.5 w-1.5 rounded-full bg-primary animate-next-up" />
          ) : null}
        </button>
      ))}
    </div>
  );
};
